import React, { useState } from "react"
import { Alert, Badge, Button, Card, CardBody, FormGroup, Input, Label } from "reactstrap"
import { UNCONFIRMED_CHANGE } from "./useLogisticsOperation"
import { actorName, logTime } from "./labels"
import { asksResend, emailColor, emailState, resendable } from "./shipmentLabels"

// The longest note, as Events refuses a longer one (INVALID_LOGISTICS_NOTE_TEXT).
const NOTE_LIMIT = 2000

// The two kinds of notes, each in its own card: the internal ones for the Sportya team only, the public ones for the organizer and the
// club, shown in SportyaOS and emailed to the organizer when asked (`emailed`, D20).
const KINDS = {
  internal: { title: "Internal notes", icon: "bx-lock-alt text-muted", count: "soft-secondary", label: "Add an internal note", hiddenLabel: true,
    intro: "For the Sportya team. Only SuperAdmins see them, here in CPanel.", placeholder: "Write a note for the Sportya team…",
    empty: "No internal notes yet.", add: "Add internal note", emailed: false },
  public: { title: "Public notes", icon: "bx-group text-primary", count: "soft-primary", label: "Add a public note", hiddenLabel: false,
    intro: "For the organizer and the club: shown first on the SportyaOS Logistics tab and on the printed sheet.",
    placeholder: "Write a note for the organizer…", empty: "No public notes yet.", add: "Add public note", emailed: true },
}

// What a public note's email came to (its latest delivery to the organizer), or that it was not emailed.
const emailBadge = email => {
  const status = email?.deliveries?.[0]?.status
  if (!status) return { text: "Not emailed", color: "secondary" }
  if (status === "noRecipient") return { text: "Not emailed: the organizer had no email", color: "secondary" }
  return { text: `Emailed to the organizer · ${emailState(status)}`, color: emailColor(status) }
}
// A failed or unconfirmed email is resent to the organizer of now, unless an email to them is being sent already.
const resendTo = email => (asksResend(email?.deliveries?.[0]?.status) ? (email.recipients || []).filter(resendable).map(recipient => recipient.email) : [])

function NoteEmail({ note, pending, resend }) {
  const { text, color } = emailBadge(note.email)
  const recipients = resendTo(note.email)
  return <>
    <Badge color={`soft-${color}`}><i className="bx bx-envelope mr-1" aria-hidden="true" />{text}</Badge>
    {recipients.length > 0 && <Button color="link" size="sm" className="p-0" disabled={pending} onClick={() => resend(note, recipients)}>Resend</Button>}
  </>
}

// One card of notes: newest first, each with its author and time, deleted but never edited (D20); a deletion stays in the action logs,
// with the note's text. A public note can also be emailed to the event's organizer (`organizer`, none without an email), and shows how
// that email went, with „Resend” for a failed or unconfirmed one.
function NotesCard({ kind, notes, organizer, timezone, pending, changeNote, resendNote }) {
  const words = KINDS[kind]
  const [text, setText] = useState("")
  const [email, setEmail] = useState(false)
  const [failure, setFailure] = useState(null)
  const [adding, setAdding] = useState(false)
  // Saved, the sheet reloads with its notes; refused or not confirmed, the card says why and keeps what was typed.
  const settle = result => {
    if (!result || result.status === "cancelled") return false
    setFailure(result.status === "saved" ? null : result.status === "unknown" ? UNCONFIRMED_CHANGE : result.message)
    return result.status === "saved"
  }
  const add = event => {
    event.preventDefault()
    setAdding(true)
    // Only a ticked box asks for the email; a public note is otherwise sent as an internal one is.
    changeNote({ action: "addNote", kind, text: text.trim(), ...(words.emailed && email ? { email: true } : {}) }).then(result => {
      setAdding(false)
      if (settle(result)) {
        setText("")
        setEmail(false)
      }
    })
  }
  const remove = note => changeNote({ action: "deleteNote", note: note.id }).then(settle)
  const resend = (note, recipients) => resendNote({ action: "resendMessage", message: "note", noteId: note.id, recipients }).then(settle)
  const id = `logistics-${kind}-note`

  return <Card tag="section" aria-label={words.title} className="logistics-note-card"><CardBody>
    <div>
      <h4 className="card-title"><i className={`bx ${words.icon} mr-1`} aria-hidden="true" />{words.title} <Badge color={words.count}
        className="ml-1">{notes.length}</Badge></h4>
      <p className="text-muted small mb-0">{words.intro}</p>
    </div>
    {notes.length ? <ul className="logistics-note-list">{notes.map(note => <li key={note.id} className="logistics-note">
      <div className="logistics-note-meta">
        <strong>{actorName(note.author)}</strong><span>{logTime(note.createdAt, timezone)}</span>
        {words.emailed && <NoteEmail note={note} pending={pending} resend={resend} />}
        <Button color="link" size="sm" className="logistics-note-delete" aria-label={`Delete ${kind} note by ${actorName(note.author)}`}
          disabled={pending} onClick={() => remove(note)}><i className="bx bx-trash" aria-hidden="true" /></Button>
      </div>
      <div className="logistics-note-text">{note.text}</div>
    </li>)}</ul> : <p className="text-muted mb-0">{words.empty}</p>}
    <form className="logistics-note-compose" onSubmit={add}>
      <Label for={id} className={words.hiddenLabel ? "sr-only" : "font-weight-medium mb-0"}>{words.label}</Label>
      {/* Locked while it saves, so nothing typed meanwhile is lost when it clears. */}
      <Input type="textarea" id={id} rows={2} maxLength={NOTE_LIMIT} placeholder={words.placeholder} value={text}
        disabled={adding} onChange={event => setText(event.target.value)} />
      <div className="logistics-note-actions">
        {words.emailed ? <FormGroup check className="mb-0">
          <Input type="checkbox" id={`${id}-email`} checked={email} disabled={adding || !organizer} onChange={event => setEmail(event.target.checked)} />
          <Label check for={`${id}-email`}>{organizer ? `Also email it to the organizer · ${organizer.name}, ${organizer.email}`
            : "Also email it to the organizer"}</Label>
          {!organizer && <div className="text-muted small">The event's organizer has no email: the note shows only in SportyaOS.</div>}
        </FormGroup> : <span className="text-muted small">Logged with your name and the time. Notes can be deleted, not edited.</span>}
        <Button type="submit" color="primary" disabled={pending || !text.trim()}>{adding ? "Adding…" : words.add}</Button>
      </div>
      {failure && <Alert color="danger" className="mb-0">{failure}</Alert>}
    </form>
  </CardBody></Card>
}

// The sheet's notes, first on the Logistics tab, whatever the state of the sheet: the internal and the public notes, side by side from
// 992 px. `resendNote` sends a public note's email again (Events resend.js).
export default function SheetNotes({ notes, timezone, pending, changeNote, resendNote }) {
  const shared = { timezone, pending, changeNote, resendNote }
  return <section className="logistics-notes" id="logistics-notes" aria-label="Notes">
    <NotesCard kind="internal" notes={notes?.internal || []} {...shared} />
    <NotesCard kind="public" notes={notes?.public || []} organizer={notes?.organizer || null} {...shared} />
  </section>
}
